/**
 * useDviMedia - Hook for managing DVI media items
 * 
 * Features:
 * - Fetch all media for a session with batch signed URLs
 * - Toggle excluded status (bad shot marking)
 * - Delete media via edge function
 * - Computed filtered lists (ungrouped, grouped, excluded)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface DviMediaItem {
  id: string;
  storage_path: string;
  media_type: 'image' | 'video';
  finding_group_id: string | null;
  is_excluded: boolean;
  captured_at: string;
  created_at: string;
  signedUrl?: string;
}

export function useDviMedia(ticketId: string, sessionId: string | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch all media for session with signed URLs (batched)
  const { data: media = [], isLoading, refetch } = useQuery({
    queryKey: ['dvi-media', ticketId, sessionId],
    queryFn: async () => {
      if (!sessionId) return [];

      const { data, error } = await supabase
        .from('ticket_dvi_media')
        .select('id, storage_path, media_type, finding_group_id, is_excluded, captured_at, created_at')
        .eq('session_id', sessionId)
        .order('captured_at', { ascending: false });

      if (error) throw error;
      if (!data || data.length === 0) return [];

      // Batch generate signed URLs (1 hour expiry)
      const paths = data.map((m) => m.storage_path);
      const { data: signedUrls, error: signError } = await supabase.storage
        .from('dvi_media')
        .createSignedUrls(paths, 3600);

      if (signError) {
        console.warn('[useDviMedia] Failed to generate signed URLs:', signError);
      }

      // Map signed URLs to media items
      const urlMap = new Map<string, string>();
      signedUrls?.forEach((item) => {
        if (item.signedUrl) {
          urlMap.set(item.path, item.signedUrl);
        }
      });

      return data.map((item) => ({
        ...item,
        signedUrl: urlMap.get(item.storage_path),
      })) as DviMediaItem[];
    },
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Toggle excluded status mutation
  const toggleExcludedMutation = useMutation({
    mutationFn: async ({ mediaId, excluded }: { mediaId: string; excluded: boolean }) => {
      const { error } = await supabase
        .from('ticket_dvi_media')
        .update({ is_excluded: excluded })
        .eq('id', mediaId);

      if (error) throw error;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['dvi-media', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['dvi-media-ungrouped', ticketId] });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update media',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  // Delete media via edge function
  const deleteMutation = useMutation({
    mutationFn: async (mediaId: string) => {
      const { data, error } = await supabase.functions.invoke('dvi-delete-media', {
        body: { media_id: mediaId },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Delete failed');
      return data;
    },
    onSuccess: () => {
      // Invalidate all media-related queries
      queryClient.invalidateQueries({ queryKey: ['dvi-media', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['dvi-media-count', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['dvi-media-ungrouped', ticketId] });
      toast({ title: 'Media deleted' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete media',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  // Computed values
  const ungroupedMedia = media.filter((m) => !m.finding_group_id && !m.is_excluded);
  const groupedMedia = media.filter((m) => m.finding_group_id && !m.is_excluded);
  const excludedMedia = media.filter((m) => m.is_excluded);
  const activeMedia = media.filter((m) => !m.is_excluded);

  return {
    media,
    isLoading,
    refetch,
    
    // Computed lists
    ungroupedMedia,
    groupedMedia,
    excludedMedia,
    activeMedia,
    
    // Counts
    totalCount: media.length,
    ungroupedCount: ungroupedMedia.length,
    groupedCount: groupedMedia.length,
    excludedCount: excludedMedia.length,
    
    // Actions
    toggleExcluded: (mediaId: string, excluded: boolean) =>
      toggleExcludedMutation.mutateAsync({ mediaId, excluded }),
    deleteMedia: (mediaId: string) => deleteMutation.mutateAsync(mediaId),
    
    // Loading states
    isToggling: toggleExcludedMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
